import {mediaSourceQualityBlocksMetrics} from "./reportingSnapshotAvailability.js";

/** Calculate a plain financial ratio such as ROAS or POAS (without ×100). */
export function divideRatio(numerator, denominator, mediaSourceQuality = null) {
    if (mediaSourceQualityBlocksMetrics(mediaSourceQuality)) return null;
    if (denominator === null || denominator === undefined || denominator <= 0) {
        return null;
    }

    if (numerator === null || numerator === undefined || !Number.isFinite(Number(numerator)) || !Number.isFinite(Number(denominator))) {
        return null;
    }

    return Number(numerator) / Number(denominator);
}

/** Calculate PNO as ad spend / revenue × 100, preserving unavailable inputs and valid zero spend. */
export function calculatePnoPercent(spendAmount, revenueAmount, mediaSourceQuality = null) {
    if (mediaSourceQualityBlocksMetrics(mediaSourceQuality)) return null;
    if (
        spendAmount === null || spendAmount === undefined || revenueAmount === null || revenueAmount === undefined
        || !Number.isFinite(Number(spendAmount)) || !Number.isFinite(Number(revenueAmount))
        || Number(spendAmount) < 0 || Number(revenueAmount) <= 0
    ) {
        return null;
    }

    return (Number(spendAmount) / Number(revenueAmount)) * 100;
}

/** Keep percentage margins unavailable when revenue cannot be a valid denominator. */
export function marginPercentWithPositiveRevenue(marginPercent, revenueAmount) {
    if (
        marginPercent === null || marginPercent === undefined
        || !Number.isFinite(Number(marginPercent))
        || revenueAmount === null || revenueAmount === undefined
        || !Number.isFinite(Number(revenueAmount)) || Number(revenueAmount) <= 0
    ) {
        return null;
    }

    return Number(marginPercent);
}

export function formatRatioMultiplier(value, decimals = 1) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
        return "-";
    }

    return `${new Intl.NumberFormat("cs-CZ", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(Number(value))}×`;
}

/** Convert an optional API money value to major units without turning absence into zero. */
export function nullableMoneyMajorUnitAmount(money) {
    if (money === null || money === undefined || money.amount === null || money.amount === undefined) {
        return null;
    }

    return Number(money.amount) / 100;
}

export function nullableMoneyMapToMajorUnitValues(moneyByKey) {
    return Object.values(moneyByKey ?? {}).map(nullableMoneyMajorUnitAmount);
}

export function legacyMarketingSalesSummaryValues(basic, isMarketing) {
    const basis = isMarketing ? "Gross" : "Net";

    return {
        turnover: basic?.[`turnover${basis}`]?.value ?? null,
        gain: basic?.[`gain${basis}`]?.value ?? null,
        gainChange: basic?.[`gain${basis}`]?.percentageChangeAgainstPreviousPeriod ?? null,
        marketingSpend: basic?.spendMarketing?.value ?? null,
        orders: basic?.[`orderAmount${basis}`]?.value ?? null,
        pno: basic?.[`pno${basis}`]?.value ?? null,
        averageOrderValue: basic?.[`averageOrderValue${basis}`]?.value ?? null,
    };
}

export function legacyMarketingSalesSpendSlices(sources) {
    const slices = (sources ?? []).filter((source) => source?.financeScope !== "raw_provider").map((source, index) => ({
        source,
        index,
        amount: nullableMoneyMajorUnitAmount(source?.spendMarketing?.value),
    }));

    if (slices.some((slice) => slice.amount === null)) {
        return null;
    }

    return slices.filter((slice) => slice.amount > 0);
}

export function averageOrderValueAmount(turnoverAmount, orderCount) {
    if (turnoverAmount === null || turnoverAmount === undefined || orderCount === null || orderCount === undefined) {
        return null;
    }

    const turnover = Number(turnoverAmount);
    const orders = Number(orderCount);
    if (!Number.isFinite(turnover) || !Number.isFinite(orders) || orders <= 0) {
        return null;
    }

    return turnover / orders;
}
